import React, { SyntheticEvent, ReactElement } from 'react';
import { getAllFormErrors, AllFormErrors, EmptyFormErrors } from '@root/utils/ErrorHelper';
import FormFooter from '@components/Form/FormFooter';
import Input, { InputTypes } from '@components/Form/Input';
import FormRow from '@components/Form/FormRow';
import { withRouter, RouteComponentProps } from 'react-router';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { compose } from 'react-apollo';
import { Notification } from 'components';
import Tab from '../Tab/Tab';

type Props<T> = {
    intlPrefix: string,
    redirectPath?: string | ((submitResponse: any) => string | undefined),
    fields: Array<TabFieldType<T>>,
    formData: T,
    footerPosition?: FormFooterPosition,
    onSubmit: (formData: T) => Promise<any>,
    onChange?: (formData: T) => any,
    validate: (formData: T) => boolean,
} & InjectedIntlProps & RouteComponentProps;

export enum FormFooterPosition {
    TOP = "TOP",
    BOTTOM = "BOTTOM",
    BOTH = "BOTH",
}

export interface TabFieldType<T> {
    id: string;
    fields: Array<(FieldType<T> | Element)>;
}

interface InputFieldType<T> {
    name: keyof T;
    type: InputTypes;
    props?: any;
}

interface ElementFieldType<T> {
    name: keyof T;
    element: Element;
    props?: any;
}

export type FieldType<T>  = InputFieldType<T> | ElementFieldType<T>;

interface State<T> {
    selected: string;
    formData: T;
    allFormErrors: AllFormErrors;
}

class Form<T> extends React.Component<Props<T>, State<T>> {

    public constructor(props: Props<T>) {
        super(props);
        this.state = {
            selected: '',
            formData: this.props.formData,
            allFormErrors: EmptyFormErrors,
        };
    }

    public componentDidMount() {
        this.setSelected();
    }

    private setSelected() {
        if (this.props.fields && Array.isArray(this.props.fields) && this.props.fields.length) {
            const tab = this.props.fields[0];
            this.setState({selected: (tab as TabFieldType<T>).id});
        }
    }

    private renderNav(): ReactElement<any> {
        return (
            <ul className="nav nav-tabs">
                {this.props.fields && Array.isArray(this.props.fields) && this.props.fields.map((tab: TabFieldType<T>) => {
                    return (
                        <li key={tab.id} className={this.state.selected === tab.id ? 'active' : ''}>
                            <a onClick={() => this.setState({selected: tab.id})}>
                                {this.props.intl.formatMessage({ id: `${this.props.intlPrefix}.tabs.${tab.id}` })}
                            </a>
                        </li>
                    );
                })}
            </ul>
        );
    }

    public componentWillReceiveProps(props: Props<T>) {
        this.setState({formData: props.formData});
    }

    public componentDidUpdate() {
        if (!this.state.selected) {
            this.setSelected();
        }
    }

    private onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        this.props.onSubmit(this.state.formData).then((response) => {
            return new Promise((resolve: (path: string | undefined) => void) => {
                this.setState({ allFormErrors: EmptyFormErrors }, () => Notification.success(this.props.intl.formatMessage({ id: 'notification.succeed' })).then(() => {
                    let redirectPath;
                    if (typeof this.props.redirectPath === "function") {
                        redirectPath = this.props.redirectPath!(response);
                    } else if (typeof this.props.redirectPath === "string") {
                        redirectPath = this.props.redirectPath!;
                    } else {
                        redirectPath = undefined;
                    }
                    resolve(redirectPath);
                }));
            });
        }).then((redirectPath: string | undefined) => {
            if (typeof redirectPath !== "undefined") {
                this.props.history.push(redirectPath!);
            }
        }).catch((e: any) => {
            const allFormErrors = getAllFormErrors(e);
            this.setState({allFormErrors});

            Notification.error(allFormErrors.general.join("\n") || this.props.intl.formatMessage({ id: 'notification.failed' }));
        });
    }

    private onChange = (value: any, name: keyof T) => {
        // @ts-ignore
        this.setState({formData: {...this.state.formData, [name]: value}}, () => {
            if (this.props.onChange) {
                this.props.onChange(this.state.formData);
            }
        });
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `${this.props.intlPrefix}.${id}` });
    }

    private getPlaceholderByName = (name: keyof T) => {
        return !!this.props.intl.messages[`${this.props.intlPrefix}.${name}.placeholder`] ?
        this.label(`${name}.placeholder`) :
        this.label(`${name}.title`);
    }

    private getHintByName = (name: keyof T) => {
        return !!this.props.intl.messages[`${this.props.intlPrefix}.${name}.hint`] ?
            this.label(`${name}.hint`) :
            undefined;
    }

    private isElementField(field: FieldType<T>): field is ElementFieldType<T> {
        return (field as ElementFieldType<T>).element !== undefined;
    }

    private getError(field: FieldType<T>): string {
        let result = '';
        if (this.state.allFormErrors && this.state.allFormErrors.field) {
            const keys = [field.name, ...((field.props && field.props.alias) ? field.props.alias : [])];
            result = this.state.allFormErrors.field.filter((value) => keys.includes(value.key)).map((value) => value.message).join(', ');
        }
        return result;
    }

    private renderFormRow(field: FieldType<T>) {
        const placeholder = this.getPlaceholderByName(field.name);
        const hint = (field.props && field.props.hint) ? field.props.hint :
            (this.getHintByName(name));

        const before = (field.props && field.props.before) ? field.props.before : null;
        const after = (field.props && field.props.after) ? field.props.after : null;

        const input = this.isElementField(field) ? field.element : <Input
                type={field.type} name={(field.name as string)}
                value={this.state.formData[field.name] ? this.state.formData[field.name] : undefined}
                onChange={this.onChange} placeholder={placeholder} hint={hint}
                {...field.props}
            />;

        const label = `${this.label(`${field.name}.title`)}${(field.props && field.props.required) ? ' *' : ''}`;

        return (
            <div key={(field.name as string)}>
                {before}
                <FormRow label={label} error={this.getError(field)}>
                    {input}
                </FormRow>
                {after}
            </div>
        );
    }

    private shouldRenderTopFooter = () => {
        return this.props.footerPosition && [FormFooterPosition.TOP, FormFooterPosition.BOTH].includes(this.props.footerPosition);
    }

    private shouldRenderBottomFooter = () => {
        return !this.props.footerPosition || [FormFooterPosition.BOTTOM, FormFooterPosition.BOTH].includes(this.props.footerPosition);
    }

    private renderFormFooter = (): JSX.Element => {
        return (
            <FormFooter
                onCancel={(e: SyntheticEvent) => { e.preventDefault(); this.props.history.goBack(); }}
                validate={() => this.props.validate(this.state.formData)}
            />
        );
    }

    private renderTabs() {
        return (
            <form className="form-horizontal" onSubmit={this.onSubmit}>

                <div className="tabs-container">
                    {this.renderNav()}

                    {this.shouldRenderTopFooter() && (
                        <div className="tab-footer m-t-md">
                            {this.renderFormFooter()}
                        </div>
                    )}

                    <div className="tab-content">

                        {this.props.fields.map((tab: TabFieldType<T>) => {
                            if (this.state.selected === tab.id) {
                                return (
                                    <Tab id={tab.id} key={tab.id} label={this.props.intl.formatMessage({ id: `${this.props.intlPrefix}.tabs.${tab.id}` })}>
                                        {tab.fields.map((field: FieldType<T> | Element, index: number) => {
                                            return (
                                                <div key={`${tab.id}-${index}`}>
                                                    {typeof (field as FieldType<T>).name === 'string' ?
                                                        this.renderFormRow((field as FieldType<T>)) :
                                                        field
                                                    }
                                                </div>
                                            );
                                        })}
                                    </Tab>
                                );
                            }
                            return <span key={tab.id} />;
                        })}

                        {this.props.children}
                    </div>

                    {this.shouldRenderBottomFooter() && (
                        <div className="tab-footer m-t-md">
                            {this.renderFormFooter()}
                        </div>
                    )}

                </div>
            </form>
        );
    }

    private renderForm() {
        const fieldGroup = this.props.fields[0];
        return (
            <form className="form-horizontal" onSubmit={this.onSubmit}>
                {this.shouldRenderTopFooter() && this.renderFormFooter()}

                {fieldGroup.fields.map((field: FieldType<T>) => this.renderFormRow(field))}
                {this.props.children}

                {this.shouldRenderBottomFooter() && this.renderFormFooter()}
            </form>
        );
    }

    public render() {

        if (this.props.fields.length === 1) {
            return this.renderForm();
        }

        return this.renderTabs();
    }

}

export default compose(
    injectIntl,
    withRouter,
)(Form);



// WEBPACK FOOTER //
// ./src/components/Form/Form.tsx